import type { QueryClient } from '@tanstack/react-query';
import { charactersEntity } from '@/data/entities/characters';
import { tagCategoryId } from '@/enums';
import { findFountainSceneQueries, findScenePresentationQueries, rollbackQueries } from '@/hooks/optimisticHelpers';
import type { ICharacter, IEntityTag } from '@/types';

interface TagUpdateParams {
    queryClient: QueryClient;
    sceneId: number;
    projectId: number;
    version?: string;
    data: { add_tags?: number[]; remove_tags?: number[] };
    characterInfo?: IEntityTag;
    tagInfo?: IEntityTag[];
}

/**
 * Applies optimistic tag updates across both standard scene presentation
 * and fountain scene query caches. Returns previous data for rollback.
 */
export function applyTagOptimisticUpdate({
    queryClient,
    sceneId,
    projectId,
    version,
    data,
    characterInfo,
    tagInfo,
}: TagUpdateParams): Array<{ queryKey: readonly unknown[]; data: unknown }> {
    const getCharacterData = () => queryClient.getQueryData<ICharacter[]>(charactersEntity.fetchers.list.key({ projectId, version })) || [];

    // Generic updater that works for both IScenePresentationDuration and IFountainScene
    // Both have { id: number; tags: Array<{ id: number; ... }> }
    const updateSceneTags = <T extends { id: number; tags: IEntityTag[] }>(scenes: T[]): T[] =>
        scenes.map(scene => {
            if (scene.id !== sceneId) return scene;

            const updatedTags: IEntityTag[] = [...scene.tags];

            if (data.add_tags) {
                const allCharacters = getCharacterData();

                for (const tagId of data.add_tags) {
                    if (updatedTags.some(tag => tag.id === tagId)) continue;

                    if (characterInfo && characterInfo.id === tagId) {
                        updatedTags.push(characterInfo);
                    } else {
                        const character = allCharacters.find(char => char.entity_tag.id === tagId);
                        if (character?.entity_tag) {
                            updatedTags.push(character.entity_tag);
                        } else {
                            const fromTagInfo = tagInfo?.find(t => t.id === tagId);
                            if (fromTagInfo) {
                                updatedTags.push(fromTagInfo);
                            } else if (characterInfo) {
                                updatedTags.push({
                                    id: tagId,
                                    name: 'New Character',
                                    tag_category_id: tagCategoryId.characterTag,
                                    color: '#000000',
                                });
                            }
                        }
                    }
                }
            }

            if (data.remove_tags) {
                return {
                    ...scene,
                    tags: updatedTags.filter(tag => !data.remove_tags?.includes(tag.id)),
                };
            }

            return { ...scene, tags: updatedTags };
        });

    const previousData: Array<{ queryKey: readonly unknown[]; data: unknown }> = [];

    // Update standard scene presentation queries
    const presentationQueries = findScenePresentationQueries(queryClient);
    for (const [queryKey, queryData] of presentationQueries) {
        if (queryData && Array.isArray(queryData)) {
            previousData.push({ queryKey, data: queryData });
            queryClient.setQueryData(queryKey, (old: unknown) => (Array.isArray(old) ? updateSceneTags(old) : old));
        }
    }

    // Update fountain scene queries
    const fountainQueries = findFountainSceneQueries(queryClient);
    for (const [queryKey, queryData] of fountainQueries) {
        if (queryData && Array.isArray(queryData)) {
            previousData.push({ queryKey, data: queryData });
            queryClient.setQueryData(queryKey, (old: unknown) => (Array.isArray(old) ? updateSceneTags(old) : old));
        }
    }

    return previousData;
}

/** Rollback previously captured tag optimistic updates */
export function rollbackTagOptimisticUpdate(
    queryClient: QueryClient,
    previousData: Array<{ queryKey: readonly unknown[]; data: unknown }>,
) {
    rollbackQueries(queryClient, previousData);
}
